<template lang="pug">
.index-documentary
  .z-top
    .header-photo
      img.bg(:src="header.photo" alt="")
      //- The title is artwork here, with no text heading, so the image carries it
      img.text(:src="header.text" :alt="postTitle")
    RogueButton.watch(color="black" right-icon="arrow-right" @click="showVideo = url") {{header.btn}}
    .main-container-small.top-text.z-top
      .author {{author}}
      .date
        span.published(v-if="post.date") {{ `${__('Published')}: ${publishedDate}` }}
        span(v-if="showModifiedDate") &nbsp;&vert;&nbsp;
        span.modified(v-if="showModifiedDate") {{ `${__('Updated')}: ${modifiedDate}` }}
      br
      br
      p(v-for="p in topContent" :key="p" v-html="p" v-translate="`theindex_${post.id}`")
    hr(v-if="hasMap")
  .map-content(v-if="hasMap")
    .z-top
      h2 {{map.header}}
      p {{map.sub}}
    .pills.z-top(v-if="map.countries.length > 1" ref="pills")
      .pill(
        v-for="country in map.countries"
        :key="country.name"
        @click="setCountry(country)"
        :class="activeMap === country && 'active'"
      )
        | {{country.name}}
      .active-pill(:style="activeStyle")
    .maps(:style="{left: -100 * activeMap.index + '%' }" :class="'post-'+post.id")
      .height
      .map(
        v-for="(country,i) in map.countries"
        :key="country.name"
        :style="{left: i * 100 +'%'}"
        :class="'map-'+(country.name || post.id)"
      )
        .map-container
          img.map-img(:src="country.src" :alt="country.name ? __('Map of {country}', { country: country.name }) : ''")
        .tips-container
          img.map-img(:src="country.src" alt="" style="opacity: 0; pointer-events: none")
          .tip-position
            .mobile-tips
              img.mobile-map-tip.pin-img(
                v-for="(pin, i) in country.pins"
                :src="activeTip === pin ? pinActiveSrc : pinSrc"
                :alt="pin.title"
                @click="activateTip(pin)"
                :key="i"
                :style="pin.style"
              )
            .desktop-tips
              tooltip.map-tip(
                v-for="(pin,i) in country.pins"
                :key="i"
                :style="pin.style"
                :placement="pin.placement"
              )
                img.pin-img( :src="desktopTip === pin ? pinActiveSrc : pinSrc" :alt="pin.title"  @mouseover="desktopTip = pin" @mouseout="desktopTip = null" )
                template(#tooltip)
                  .pin-content
                    img(:src="pin.src" v-if="pin.src" alt="")
                    h4 {{pin.title}}
                    h5.subtitle(v-if="pin.subtitle") {{pin.subtitle}}
                    .text(v-html="pin.content" v-translate="`theindex_${post.id}`")
  .bottom-content.z-top
    Carousel.mobile-tips(:snap="true" ref="mobileTips" controls="inside" @change="activateTip(allPins[$event])")
      .mobile-tip(v-for="(pin, i) in allPins" :key="i")
        img(:src="pin.src" v-if="pin.src" alt="")
        h4 {{pin.title}}
        h5.subtitle(v-if="pin.subtitle") {{pin.subtitle}}
        .text(v-html="pin.content" v-translate="`theindex_${post.id}`")
    hr.after-carousel(v-if="extras.title")
    h2(v-if="extras.title") {{extras.title}}
    .extras
      .extra(v-for="vid in extras.content" :key="vid.url")
        .extra-img
          img(:src="vid.thumbnail" alt="" @click="showVideo = vid.url")
        h5.title {{vid.title}}
        .desc {{vid.desc}}
    .docu-gallery(v-if="gallery.images.length")
      hr
      h2 Image Gallery
      .images
        img(
          v-for="thumb in gallery.thumbs"
          :key="thumb.src"
          :src="thumb.src"
          :alt="__('Gallery image {n}', { n: String(thumb.galleryLocation) })"
          @click="showImgIndex = thumb.galleryLocation - 1; showGallery = true"
        )
      RogueButton.view-gallery(
        color="black"
        right-icon="arrow-right"
        @click="showImgIndex = 0; showGallery = true;"
      ) View Gallery
  CarouselModal(v-if="showGallery" :value="showImgIndex" @close="showGallery = false")
    img.slide-img(v-for="(img, i) in gallery.images" :src="img" :alt="__('Gallery image {n}', { n: String(i + 1) })")
  youtube-video-overlay(v-model="showVideo")
</template>
<script setup lang="ts">
import { computed, nextTick, reactive, ref, useTemplateRef, watch } from 'vue'
import type Carousel from 'sisu/components/core/Carousel.vue'
import type { ITheIndexPost } from '../types'
import YoutubeVideoOverlay from '@/js/pages/theindex/components/youtube-video-overlay.vue'
import pinSrc from '@/assets/map-pin.svg'
import pinActiveSrc from '@/assets/map-pin-active.svg'
import { useRogue } from '@/js/services/Rogue'
import { formatDate } from '@/js/helpers/date'

interface Pin {
  style: {
    left: string,
    top: string,
  }
  src?: string,
  content: string,
  placement: string,
  title: string,
  subtitle?: string,
}

interface Country {
  name: string,
  index: number,
  src: string,
  pins: Pin[],
}

interface Video {
  url: string,
  thumbnail: string,
  title: string,
  desc: string,
}

interface Thumbnail {
  src: string,
  galleryLocation: number,
}

interface DocumentaryData {
  headerData?: {
    headerVideoUrl?: string,
    headerPhoto?: string,
    headerPhotoTextUrl?: string,
    headerBtnText?: string,
  },
  pageContent?: {
    topDocSummary?: string,
    topDocSummary2?: string,
    mapHeader?: string,
    mapSubHeader?: string,
    multiMap?: boolean,
    mapImage?: string,
    mapImages?: string[],
    countries?: Array<{
      country: string,
      start: number,
      end: number,
    }>,
    midHeader?: string,
  },
  mapPins?: Array<{
    left: string,
    top: string,
    image?: string,
    description: string,
    tooltip: {
      location?: string,
    },
    name: string,
    subtitle?: string,
  }>,
  videos?: Video[],
  bottomGallery?: string[],
  bottomGalleryThumbnails?: Thumbnail[],
}

const rogue = useRogue()

const props = withDefaults(defineProps < {
  post?: ITheIndexPost,
  authorList?: string[],
}>(), {
  post       : () => ({} as ITheIndexPost),
  authorList : () => ([]),
})

const pillsRef = useTemplateRef<HTMLDivElement>('pills')
const mobileTipsRef = useTemplateRef<typeof Carousel>('mobileTips')

// post.title.rendered is HTML from WordPress; alt text needs it as plain text
const postTitle = computed(() => (props.post.title?.rendered || '')
  .replace(/<[^>]*>/g, '')
  .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
  .replace(/&amp;/g, '&')
  .replace(/&quot;/g, '"'))

const header = reactive({
  photo : null as string | null,
  text  : null as string | null,
  btn   : null as string | null,
})

const topContent = ref<string[]>([])

const extras = reactive({
  title   : null as string | null,
  content : [] as Video[],
})

const gallery = reactive({
  images : [] as string[],
  thumbs : [] as Thumbnail[],
})

const map = reactive({
  header    : null as string | null,
  sub       : null as string | null,
  countries : [] as Country[],
})

const allPins = ref<Pin[]>([])
const url = ref<string | null>(null)
const data = ref<DocumentaryData | null>(null)

const activeMap = ref<Country>({ index: 0, name: '', src: '', pins: [] })
const activeTip = ref<Pin | null>(null)
const desktopTip = ref<Pin | null>(null)
const activeStyle = reactive({
  left  : '0px',
  width : '0px',
})

const showImgIndex = ref<number | null>(null)
const showGallery = ref<boolean>(false)
const showVideo = ref<string | null>(null)

const hasMap = computed<boolean>(() => map.countries.length > 0)
const author = computed<string | null>(() => {
  const name = props.post._embedded?.author?.[0]?.name
  if (!name || !props.authorList.length) return null
  const lower = name.toLowerCase()

  return props.authorList.some(auth => lower.includes(auth)) ? name : null
})
const publishedDate = computed<string>(() => formatDate(props.post.date, rogue))
const modifiedDate = computed<string>(() => formatDate(props.post.modified, rogue))
const showModifiedDate = computed<boolean>(() => publishedDate.value !== modifiedDate.value)


watch(() => props.post.id, () => {
  load()
})

load()

function load() {
  const postData = JSON.parse(props.post.customJs!)
  data.value = postData

  // Header
  const {
    headerVideoUrl,
    headerPhoto,
    headerPhotoTextUrl,
    headerBtnText = 'Watch the Film',
  } = (postData.headerData || {})
  header.photo = headerPhoto
  header.text = headerPhotoTextUrl
  header.btn = headerBtnText
  url.value = headerVideoUrl

  // Top Text
  const { topDocSummary, topDocSummary2 } = (postData.pageContent || {})
  topContent.value = [topDocSummary, topDocSummary2].filter(val => !!val)

  // Map
  const pins = (postData.mapPins || []).map((p: any) => ({
    style: {
      left : p.left,
      top  : p.top,
    },
    src       : p.image,
    content   : p.description,
    placement : p.tooltip.location || 'left',
    title     : p.name,
    subtitle  : p.subtitle,
  }))

  allPins.value = pins
  activeTip.value = pins[0]

  const { mapHeader, mapSubHeader, multiMap, mapImage, mapImages, countries } = (postData.pageContent || {})
  map.header = mapHeader
  map.sub = mapSubHeader

  if (multiMap) {
    map.countries = countries.map((c: any, index: number) => ({
      name : c.country,
      index,
      src  : mapImages[index],
      pins : pins.slice(c.start, c.end + 1),
    })).filter((m: any) => m.pins.length > 0)
    nextTick(() => setCountry())
  } else if (mapImage) {
    map.countries = [{ name: '', index: 0, src: mapImage, pins }]
  }

  // Extras
  const { videos } = postData
  extras.title = postData.pageContent.midHeader
  extras.content = videos

  // gallery
  const { bottomGallery, bottomGalleryThumbnails } = postData
  gallery.images = bottomGallery
  gallery.thumbs = bottomGalleryThumbnails
}

function setCountry(country = map.countries[0]) {
  if (activeMap.value === country || map.countries.length === 1) {
    return
  }
  activeMap.value = country
  if (rogue.ssr) {
    activeStyle.left = '0px'
    activeStyle.width = '100px'
  } else {
    const activePillEl = pillsRef.value?.children[country.index]
    activeStyle.left = `${(activePillEl as HTMLElement).offsetLeft}px`
    activeStyle.width = `${(activePillEl as HTMLElement).clientWidth}px`
  }
}

function activateTip(pin: Pin) {
  activeTip.value = pin
  const country = map.countries.find(c => c.pins.includes(pin))

  setCountry(country)
  mobileTipsRef.value?.scrollTo(allPins.value.indexOf(pin), true)
}
</script>
<style lang="scss" scoped>
.index-documentary {
  overflow: hidden;
}

.header-photo {
  display: flex;
  justify-content: center;
  align-items: center;
  // Reserves the header's height before the image loads
  aspect-ratio: 2.846 / 1;

  .bg {
    width: 100%;
    height: auto;
  }

  .text {
    width: 65%;
    max-width: 800px;
    position: absolute;
  }
}

.z-top {
  z-index: 1;
  position: relative;
}

.watch {
  margin: -24px auto 0 auto;
}

.top-text {
  text-align: center;
  margin-top: $space-l;
}

hr {
  border-top: 1px solid $color-black-light;
  margin: $space-xxl auto;
  max-width: 897px;
  @include mobile() {
    margin: $space-xxl $space-m;

    &.after-carousel {
      margin-top: 0;
    }
  }

}

.map-content {
  text-align: center;
  z-index: 1;
}

.pills {
  display: inline-flex;
  height: 40px;
  background-color: white;
  align-items: center;
  margin: $space-s auto 0;
  position: relative;
  border-radius: 20px;
  border: 1px solid $color-gray-light;
  z-index: 2;

  .pill {
    z-index: 1;
    line-height: 40px;
    cursor: pointer;
    text-transform: uppercase;
    padding: 0 $space-s;
    font-size: $size-xs;
    font-family: $font-condensed;
    color: $color-black-light;
    font-weight: $weight-bold;
    transition: all ease 0.3s;

    &.active {
      color: $color-white;
    }
  }

  .active-pill {
    z-index: 0;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    background-color: $color-blue;
    border-radius: 20px;
    transition: all ease 0.3s;
  }
}

.subtitle {
  margin-bottom: 6px;
}

.maps {
  position: relative;
  transition: left ease 0.3s;
  pointer-events: none;

  .height {
    padding-top: 50%;
    pointer-events: none;
  }

  .map {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
  }

  .map-container, .tips-container {
    max-width: 1500px;
    position: absolute;
    width: 100%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    .map-img {
      width: 100%;
      z-index: 1;
    }
  }

  .tips-container {
    z-index: 4;
  }

  .tip-position {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    position: absolute;
  }

  .pin-img {
    width: 36px;
    height: 36px;
    margin-top: -36px;
    margin-left: -18px;
  }

  .desktop-tips {
    display: none;
    @include not-mobile() {
      display: block;
    }
  }

  .map-tip, .mobile-map-tip {
    pointer-events: all;
    position: absolute;
    cursor: pointer;
  }

  :deep() {
    .tip {
      max-width: 312px;
      background-color: white;
    }

    .arrow {
      width: 20px;
      height: 20px;

      &:before {
        background-color: white;
        width: 20px;
        height: 20px;
      }
    }

    .tip[data-popper-placement^='left'] > .arrow {
      right: 0;
    }

    .tip[data-popper-placement^='right'] > .arrow {
      left: -20px;
    }
  }

  .pin-content {
    z-index: 2;
    width: 250px;
    text-align: left;

    img {
      width: 250px;
    }

    h4 {
      margin: $space-xs 0;
    }

    .text {
      font-size: $size-xs;
      line-height: $line-height-m;
    }
  }
}

.mobile-tips {
  padding: $space-m $space-s;

  .mobile-tip {
    text-align: left;
    width: 100%;
    padding: $space-xs;

    img {
      width: 100%;
      margin-bottom: $space-xxs;
    }
  }

  @include not-mobile() {
    display: none;
  }

  :deep() {
    .contents {
      padding-bottom: $space-xs;
    }

    .controls {
      display: flex;
    }
  }
}

.extras, .docu-gallery .images {
  flex-wrap: wrap;
  justify-content: space-between;
  margin: 0 auto;
  overflow: hidden;
  width: 929px;
  max-width: 100%;
  padding: 0 $space-s;

  &.images {
    display: inline-flex;

    img {
      max-width: 33%;
      cursor: pointer;
    }
  }

  @include not-mobile() {
    padding: 0;
    display: inline-flex;
  }

  > .extra, > img {
    flex-basis: 33%;
    flex-grow: 1;
    text-align: left;
    padding: $space-xxs;

    @include not-mobile() {
      padding: $space-xs;
    }
  }
}

.bottom-content {
  text-align: center;
}

.extras {
  .extra {
    img {
      width: 100%;
      cursor: pointer;
      margin-bottom: $space-xxs;
    }

    .extra-img {
      position: relative;
    }

    .extra-img:after {
      position: absolute;
      content: "";
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: url(../assets/play-btn.svg) no-repeat;
      background-size: contain;
      width: 2em;
      height: 2.5em;
      pointer-events: none;
    }

    .title {
      margin-bottom: $space-xxs;
    }

    .desc {
      line-height: $line-height-l;
    }
  }
}

.docu-gallery {
  margin-top: $space-m;

  img {
    cursor: pointer;
  }

  .view-gallery {
    margin: $space-m auto;
  }
}

// Apollon
.post-17604 .tip-position {
  top: 8%;
  bottom: 17%;
  right: -2%;
}

.post-17604 .maps .height {
  padding-bottom: 100%;
}

// Sandwina
.post-32024 {
  .map-USA {
    top: -31%;

    .tip-position {
      top: 28%;
      bottom: 15%;
    }
  }

  .map-Europe {
    .tip-position {
      top: 38%;
      bottom: 15%;
      left: 6%;
      right: 7%;
    }
  }
}

// Saxon
.post-33705 .tip-position {
  top: 39%;
  bottom: 15%;
}

//Hack
.post-17577 .tip-position {
  top: 39.5%;
  left: 5%;
  right: 5%;
  bottom: 20%;
}

// fullsterkur
.post-17600 .tip-position {
  bottom: 5%;
}

// stoneland
.post-17598 {
  .tip-position {
    top: 24%;
    bottom: 32%;
  }
}

// sandow
.post-17602 {
  .map-Belgium .tip-position {
    bottom: 20%;
  }

  .map-France .tip-position {
    bottom: 20%;
  }
}

.slide-img {
  max-height: 85%;
  max-width: 85%;
}

.date{
  font-size: $size-xs;
  color: $color-gray-x-dark;
  text-align: justify;

  .published, .modified {
    font-style: italic;    
  }
}
.author{
  text-align: justify;
  font-style: italic;
}
</style>
